<template>
  <!-- Table Container Card -->
  <b-card>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_AUDIT_TRAIL')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport()">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport()">
              Excel
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="auditTrailRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="cachedAuditTrailList ? cachedAuditTrailList.data : auditTrailList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="d-flex justify-content-center">
          <feather-icon
            icon="EyeIcon"
            class="cursor-pointer"
            @click="onSelectAuditTrailItem(data.item)"
          />
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="$t('Cancel')"
      ok-only
      ok-variant="outline-secondary"
      centered
      size="lg"
    >
      <div class="text-center">
        <h3>{{ modalHeader.dataOperationType }}  - {{ modalHeader.entity }} - {{ modalHeader.subjectIdentifier }}</h3>
        <p>{{ modalHeader.fullName }}</p>
        <p>{{ modalHeader.dateCreated }}</p>
        <p>{{ modalHeader.requestHostIPAddress }}</p>
      </div>

      <b-table
        ref="viewAuditTrailTable"
        :items="viewAuditTrailList"
        :per-page="pageSize"
        :current-page="currentPage"
        fixed
        :fields="translatedViewAuditTrailTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #cell(description)="data">
          {{ addSpaceToString(data.value) }}
        </template>

        <template #cell(oldValue)="data">
          <div v-if="Array.isArray(data.value)">
            <div
              v-for="(item, index) in data.value"
              :key="index"
            >
              <p
                v-for="([text, value], currentIndex) in Object.entries(item)"
                :key="currentIndex"
                class="text-danger"
              >
                <span>{{ text }}:  {{ value }}</span>
              </p>
            </div>
          </div>

          <b
            v-else
            :class="data.item.oldValue !== data.item.newValue ? 'text-danger break' : 'break'"
            style=""
          >{{ data.value }}</b>

        </template>

        <template #cell(newValue)="data">
          <div v-if="Array.isArray(data.value)">
            <div
              v-for="(item, index) in data.value"
              :key="index"
            >
              <p
                v-for="([text, value], currentIndex) in Object.entries(item)"
                :key="currentIndex"
                class="text-success"
              >
                <span>{{ text }}:  {{ value }}</span>
              </p>
            </div>
          </div>
          <b
            v-else
            :class="data.item.oldValue !== data.item.newValue ? 'text-success break' : 'break'"
          >{{ data.value }}</b>
        </template>
      </b-table>
    </b-modal>

    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchAuditTrails"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Username -->
        <b-col md="6">
          <SelectField
            id="username"
            v-model="filter.username"
            :label="$t('Initiator')"
            :placeholder="$t('Select')"
            :options="initiatorIDOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Username -->

        <!-- Entity -->
        <b-col md="6">
          <InputField
            id="fullName"
            v-model="filter.fullName"
            :label="$t('Full Name')"
            :placeholder="$t('Enter Full Name')"
            type="text"
          />
        </b-col>
        <!-- Entity -->

        <!-- Data Operation Type -->
        <b-col md="6">
          <SelectField
            id="data-operation-type"
            v-model="filter.dataOperationType"
            :label="$t('Data Operation Type')"
            :placeholder="$t('Select')"
            :options="operationTypeOptions"
            :reduce="option => option"
            item="label"
          />
        </b-col>
        <!-- Data Operation Type -->

        <!-- Subject Identifier -->
        <b-col md="6">
          <InputField
            id="subject-identifier"
            v-model="filter.subjectIdentifier"
            :label="$t('Subject Identifier')"
            :placeholder="$t('Enter subject identifier')"
            type="text"
            for="subject-identifier"
          />
        </b-col>
        <!-- Subject Identifier -->

        <!-- Subject Name -->
        <b-col md="6">
          <InputField
            id="entity"
            v-model="filter.entity"
            :label="$t('Subject Name')"
            :placeholder="$t('Enter Subject Name')"
            type="text"
            for="entity"
          />
        </b-col>
        <!-- Subject Name -->

        <!-- Function -->
        <b-col md="6">
          <SelectField
            id="action"
            v-model="filter.action"
            :label="$t('Function')"
            :placeholder="$t('Select')"
            :options="functionOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Function -->

        <!-- Creation Date -->
        <b-col md="6">
          <label for="creation-date"> {{ $t('Creation Date') }} </label>
          <b-form-datepicker
            id="creation-date"
            v-model="filter.startDate"
            :placeholder="$t('Enter Creation Date')"
            local="en"
            :min="minFilterDate"
            :max="maxFilterDate"
          />
        </b-col>
        <!-- Creation Date -->

        <!-- Initiator IP -->
        <b-col md="6">
          <InputField
            id="initiatorIP"
            v-model="filter.requestHostIPAddress"
            :label="$t('Initiator IP')"
            :placeholder="$t('Enter Initiator IP')"
            type="text"
            for="initiatorIP"
          />
        </b-col>
        <!-- Initiator IP -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BFormDatepicker, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import moment from 'moment'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { operationType } from '../../../helpers/data'
import addSpaceToString from '../../../helpers/addSpaceToString'
import Button from '../../components/button/Button.vue'
import { operationPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    TableEntries,
    Button,
    BTable,
    TablePagination,
    BModal,
    BSpinner,
    InputField,
    SelectField,
    BFormDatepicker,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, permissionMixin, sweetAlert],
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    // 1st 2021
    const minDate = new Date(2021, 1, 1)

    // today
    const maxDate = new Date(today)
    return {
      minFilterDate: minDate,
      maxFilterDate: maxDate,
      modalHeader: {
        fullName: '',
        entity: '',
        dataOperationType: '',
        subjectIdentifier: '',
        dateCreated: '',
        requestHostIPAddress: '',
      },
      isReadonly: false,
      isFullName: false,
      statusFilter: '',
      showFormErrorAlert: false,
      sortBy: 'fullName',
      required,
      addSpaceToString,
      operationTypeOptions: operationType,
      initiatorIDOptions: [],
      functionOptions: [],
      modalTitle: this.$t('Audit Trail'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'username', sortable: true, label: 'Initiator ID' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'entity', sortable: true, label: 'Subject Name' },
        { key: 'action', sortable: true, label: 'Function' },
        { key: 'dataOperationType', sortable: true, label: 'Operation Type' },
        { key: 'subjectIdentifier', sortable: true, label: 'Subject Identifier' },
        { key: 'dateCreated', sortable: true, label: 'Creation Date' },
        { key: 'requestHostIPAddress', sortable: true, label: 'Initiator IP' },
        { key: 'actions', label: 'Actions' },
      ],
      viewAuditTrailTableHeaders: [
        {
          key: 'description', sortable: false, thStyle: { width: '20%' }, label: 'Description',
        },
        {
          key: 'oldValue', sortable: false, thStyle: { width: '40%' }, label: 'Old Value',
        },
        {
          key: 'newValue', sortable: false, thStyle: { width: '40%' }, label: 'New Value',
        },
      ],
      viewAuditTrailList: [],
      buttonLabel: this.$t('Save'),
      accountOfficerFormData: {
        lastName: '',
        otherNames: '',
        staffID: '',
        phoneNumber: '',
        accessLevel: null,
        gender: null,
        address: '',
        branchCode: '',
        status: '',
        id: '',
      },
      filter: {
        username: null,
        fullName: null,
        dataOperationType: null,
        subjectIdentifier: null,
        entity: null,
        data: null,
        action: null,
        startDate: null,
        requestHostIPAddress: null,
      },
      auditTrailList: [],
      oldData: [],
      newData: [],
      isDisabled: false,
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedViewAuditTrailTableHeaders() {
      return this.viewAuditTrailTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedAuditTrailList() {
      // Ensure tableKey matches everywhere
      return this.getTableCache('auditTrail', this.cacheKey)
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchAuditTrail()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchAuditTrail()
    },
  },
  mounted() {
    this.serveCacheAndFetchAuditTrail()
    this.fetchAuditUsers()
  },

  methods: {
    ...mapActions({
      ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),
      async serveCacheAndFetchAuditTrail() {
        // Serve cache if available
        const { cacheKey } = this
        const cache = this.getTableCache('auditTrail', cacheKey)

        if (cache && cache.data) {
          this.auditTrailList = cache.data
          this.totalCount = cache.totalCount
          this.isBusy = false
        } else {
          // No cache, show spinner and fetch
          this.isBusy = true
          await this.fetchAuditTrails(false)
        }
      },
      getAuditTrails: 'operations/getAuditTrails',
      getAuditUsers: 'operations/getAuditUsers',
      downloadAuditTrailReport: 'reports/downloadAuditTrailReport',
    }),
    onSelectAuditTrailItem(items) {
      this.modalHeader.fullName = items.fullName
      this.modalHeader.entity = items.entity
      this.modalHeader.dataOperationType = items.dataOperationType
      this.modalHeader.subjectIdentifier = items.subjectIdentifier
      this.modalHeader.dateCreated = items.dateCreated
      this.modalHeader.requestHostIPAddress = items.requestHostIPAddress
      const newData = JSON.parse(items.newData)
      const oldData = JSON.parse(items.oldData)

      newData.DateCreated = this.formatDateTime(newData.DateCreated)
      if (newData.DateLastModified) {
        newData.DateLastModified = this.formatDateTime(newData.DateLastModified)
      }
      if (oldData !== null) {
        oldData.DateLastModified = oldData.DateLastModified !== null ? this.formatDateTime(oldData.DateLastModified) : oldData.DateLastModified
        oldData.DateCreated = this.formatDateTime(oldData.DateCreated)
      }
      this.viewAuditTrailList = []
      // eslint-disable-next-line array-callback-return
      Object.entries(newData).reduce((accumulator, currentValue) => {
        if (oldData) {
          Object.entries(oldData).forEach(item => {
            if (item[0] === currentValue[0]) {
              // eslint-disable-next-line no-unused-expressions
              const newValue = currentValue[1]?.toString().includes('[{') ? JSON.parse(currentValue[1]) : currentValue[1]
              const oldValue = item[1]?.toString().includes('[{') ? JSON.parse(item[1]) : item[1]
              const val = { description: currentValue[0], newValue, oldValue }
              this.viewAuditTrailList.push(val)
            }
          })
        } else {
          const val = { description: currentValue[0], newValue: currentValue[1], oldValue: '' }
          this.viewAuditTrailList.push(val)
        }
      }, [])

      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    formatDateTime(value) {
      return moment(value).format('MMM Do YYYY, h:mm:ss a')
    },
    async fetchAuditTrails(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.$bvModal.hide('modal-filter')
      }
      try {
        const response = await this.getAuditTrails({
          search: {
            ...this.filter,
            endDate: this.filter.startDate || null,
          },
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response

        if (status === 200 && data.data) {
          // If data is different from cache, update UI
          const { cacheKey } = this
          const cache = this.getTableCache('auditTrail', cacheKey)
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            const functionKeys = ['action']
            const functionValues = data.data.filter(
              (s => o => (k => !s.has(k) && s.add(k))(functionKeys.map(k => o[k]).join('|'))
              )(new Set()),
            )
            this.functionOptions = functionValues.map(item => ({ label: item.action, value: item.action }))
            this.auditTrailList = data.data.map(items => ({
              ...items,
              entity: items.entity.match(/[A-Z][a-z]+/g).join(' '),
              dateCreated: items.dateCreated !== null ? this.formatDateTime(items.dateCreated) : '',
              dateLastModified: items.dateLastModified !== null ? this.formatDateTime(items.dateLastModified) : '',
            }))
            this.totalCount = data.totalItems
            // If on page 1 and data changed, mark all audit trail cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('auditTrail')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'auditTrail',
            cacheKey,
            payload: {
              data: data.data,
              totalCount: data.totalItems,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        if (!isBackground) this.isBusy = false
      }
    },
    async fetchAuditUsers() {
      try {
        this.isLoadingInitiatorName = true
        const response = await this.getAuditUsers({
          permission: operationPermissions.AUDIT_TRAIL,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.initiatorIDOptions = data.data.map(item => ({ label: item.name, value: item.username }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingInitiatorName = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadAuditTrailReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: operationPermissions.AUDIT_TRAIL,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadAuditTrailReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: operationPermissions.AUDIT_TRAIL,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchAuditTrails()
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.break {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp : 10 ;
  -webkit-box-orient : vertical;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
